import { useState } from 'react';
import { DataTable } from 'components/data-table';
import { DEFAULT_SCAN_DEPTH } from 'pages/admin/utils';
import type { ScanDepth } from 'pages/admin/utils';
import { failureColumns, sampledFailureColumns } from './columns';
import { useFailureScan, useScanRequest } from './hooks';
import { ScanControls } from './scan-controls';
import { ErrorNote, ScanNote } from 'pages/admin/scan-note';
import type { FailureSetName } from './types';
import styles from './page.module.scss';

const FAILURE_SETS: { id: FailureSetName; label: string }[] = [
  { id: 'retry', label: 'Retry set' },
  { id: 'dead', label: 'Dead set' },
];

/**
 * On-demand breakdown of the retry or dead set by queue, job class and error.
 *
 * @return The failure scan section.
 */
export function FailureScan() {
  const [setName, setSetName] = useState<FailureSetName>('retry');
  const [scanDepth, setScanDepth] = useState<ScanDepth>(DEFAULT_SCAN_DEPTH);
  const { request, startScan } = useScanRequest<FailureSetName>();
  const { data, isFetching, error } = useFailureScan(request);

  const setLabel =
    FAILURE_SETS.find((set) => set.id === data?.setName)?.label ?? '';

  return (
    <section className={styles.section}>
      <h4 className={styles.sectionTitle}>Retry and Dead Sets by Error</h4>
      <p className={styles.sectionDescription}>
        Groups failed jobs by queue, job class, error class and error message
        (messages are truncated).
      </p>
      <ScanControls
        idPrefix="failure-scan"
        targetLabel="Set"
        targets={FAILURE_SETS}
        target={setName}
        onTargetChange={setSetName}
        scanDepth={scanDepth}
        onScanDepthChange={setScanDepth}
        onScan={() => startScan(setName, scanDepth)}
        loading={isFetching}
        buttonLabel="Scan Set"
      />
      {error && <ErrorNote message={error.message} />}
      {/* Always rendered, so a screen reader hears each new result note. */}
      <div aria-live="polite" data-testid="failure-scan-note">
        {data && !isFetching && (
          <ScanNote
            totals={data}
            label={setLabel}
            options={{ noun: 'entries', ungrouped: data.ungrouped }}
          />
        )}
      </div>
      {data && !isFetching && (
        <DataTable
          data={data.groups}
          columns={data.sampled ? sampledFailureColumns : failureColumns}
          getRowId={(row) =>
            [row.queue, row.jobClass, row.errorClass, row.errorMessage].join(
              '|'
            )
          }
          initialTableState={{ sortColumn: 'count', sortDirection: 'desc' }}
          config={{ sorting: true, globalSearch: true }}
        />
      )}
    </section>
  );
}
